import React, { useState, useRef, useEffect } from 'react';
import ReactGA from 'react-ga';
import './Chatbot.css';

// Helper function to detect and link email addresses in text
const linkEmailAddresses = (text) => {
  // Email regex pattern: matches standard email format
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z]{2,})/g;
  
  // Check if text contains any email addresses
  if (!emailRegex.test(text)) {
    return null; // No emails found
  }
  
  // Reset regex
  emailRegex.lastIndex = 0;
  
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = emailRegex.exec(text)) !== null) {
    // Add text before email
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Add email as link
    parts.push({
      type: 'email',
      email: match[0],
      content: match[0]
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after last email
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return parts.length > 0 ? parts : null;
};

// Helper function to render messages with HTML parsing and structured links
const renderMessage = (text, links = []) => {
  // If we have structured links, use them (preferred method)
  if (links && links.length > 0) {
    const parts = [];
    let currentIndex = 0;
    
    const sortedLinks = [...links]
      .filter(link => link.position !== undefined)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    
    sortedLinks.forEach((link, index) => {
      const linkStart = link.position || 0;
      const linkEnd = linkStart + link.text.length;
      
      if (linkStart > currentIndex) {
        // Parse HTML in text before link
        const textBefore = parseHTML(text.substring(currentIndex, linkStart));
        parts.push(...textBefore);
      }
      
      parts.push(
        <a
          key={`link-${index}`}
          href={link.href}
          target={link.target || (link.type === 'external' ? '_blank' : '_self')}
          rel={link.type === 'external' ? 'noopener noreferrer' : undefined}
          onClick={(e) => {
            // Track link click with detailed context
            const linkType = link.type || 'external';
            const linkHref = link.href || 'unknown';
            const linkText = link.text.substring(0, 50);
            ReactGA.event({
              category: 'rag_chatbot',
              action: 'link_clicked',
              label: `link_type_${linkType}|link_text_${linkText}|link_href_${linkHref.substring(0, 50)}|click_source_chatbot_message|position_${link.position || 'unknown'}`,
              value: 1
            });

            if ((link.type === 'section' || link.type === 'project') && link.href.startsWith('#')) {
              e.preventDefault();
              
              // For project links, we want to trigger hashchange properly
              // Use window.location.hash which triggers hashchange event
              if (link.type === 'project') {
                window.location.hash = link.href.replace('#', '');
              } else {
                // For section links, just scroll normally
                const element = document.querySelector(link.href);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  window.history.pushState(null, '', link.href);
                }
              }
            }
          }}
          className="chatbot-link"
        >
          {link.text}
        </a>
      );
      
      currentIndex = linkEnd;
    });
    
    if (currentIndex < text.length) {
      // Parse HTML in remaining text
      const remainingText = parseHTML(text.substring(currentIndex));
      parts.push(...remainingText);
    }
    
    return <>{parts}</>;
  }
  
  // Fallback: parse HTML directly from text
  return <>{parseHTML(text)}</>;
};

// Helper function to parse HTML tags in text and auto-link emails
const parseHTML = (htmlString) => {
  if (!htmlString) return [];
  
  // First, check if there are any email addresses to link
  const emailParts = linkEmailAddresses(htmlString);
  if (emailParts) {
    // If emails found, process each part separately
    const result = [];
    emailParts.forEach((part, index) => {
      if (part.type === 'email') {
        // Render email as a link
        result.push(
          <a
            key={`email-${index}`}
            href={`mailto:${part.email}`}
            className="chatbot-link"
          >
            {part.content}
          </a>
        );
      } else {
        // Parse HTML in text parts
        const parsedParts = parseHTMLInternal(part.content);
        result.push(...parsedParts);
      }
    });
    return result;
  }
  
  // No emails found, parse HTML normally
  return parseHTMLInternal(htmlString);
};

// Internal HTML parser (extracted from original parseHTML)
const parseHTMLInternal = (htmlString) => {
  if (!htmlString) return [];
  
  const parts = [];
  let currentIndex = 0;
  
  // Match <br> or <br/> or <br /> tags
  const brRegex = /<br\s*\/?>/gi;
  // Match <a> tags with their content
  const aRegex = /<a\s+([^>]+)>(.*?)<\/a>/gi;
  
  // Combine all matches and sort by position
  const matches = [];
  
  let brMatch;
  while ((brMatch = brRegex.exec(htmlString)) !== null) {
    matches.push({
      index: brMatch.index,
      type: 'br',
      fullTag: brMatch[0],
      length: brMatch[0].length
    });
  }
  
  let aMatch;
  while ((aMatch = aRegex.exec(htmlString)) !== null) {
    matches.push({
      index: aMatch.index,
      type: 'a',
      fullTag: aMatch[0],
      attributes: aMatch[1],
      linkText: aMatch[2],
      length: aMatch[0].length
    });
  }
  
  // Sort matches by position
  matches.sort((a, b) => a.index - b.index);
  
  matches.forEach((match, matchIndex) => {
    // Add text before this match
    if (match.index > currentIndex) {
      parts.push(htmlString.substring(currentIndex, match.index));
    }
    
    if (match.type === 'br') {
      parts.push(<br key={`br-${match.index}-${matchIndex}`} />);
    } else if (match.type === 'a') {
      const hrefMatch = match.attributes.match(/href=["']([^"']+)["']/i);
      let href = hrefMatch ? hrefMatch[1] : '#';
      let linkText = match.linkText || 'link';
      
      // Fix malformed email addresses
      if (href.startsWith('mailto:')) {
        let email = href.replace('mailto:', '');
        // Fix malformations like "myke@mmyke@myked.com" -> "myke@myked.com"
        // Remove duplicate patterns: email@email@domain -> email@domain
        email = email.replace(/^([^@]+)@([^@]+)@/i, '$1@');
        // Fix specific case: myke@mmyke@ -> myke@
        email = email.replace(/myke@m+myke@/i, 'myke@');
        href = `mailto:${email}`;
        // Also clean the link text if it contains the malformed email
        if (linkText.includes('@')) {
          linkText = linkText.replace(/myke@m+myke@myked\.com/i, 'myke@myked.com');
          linkText = linkText.replace(/^([^@]+)@([^@]+)@/i, '$1@');
        }
      }
      
      const isExternal = href.startsWith('http') && !href.includes('myked.com');
      const isHashAnchor = href.startsWith('#');
      const isEmail = href.startsWith('mailto:');
      
      parts.push(
        <a
          key={`html-link-${match.index}-${matchIndex}`}
          href={href}
          target={isExternal ? '_blank' : '_self'}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          onClick={(e) => {
            if (isHashAnchor) {
              e.preventDefault();
              const element = document.querySelector(href);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.history.pushState(null, '', href);
              }
            }
            // Email links don't need preventDefault - let browser handle mailto:
          }}
          className="chatbot-link"
        >
          {linkText}
        </a>
      );
    }
    
    currentIndex = match.index + match.length;
  });
  
  // Add remaining text after last HTML tag
  if (currentIndex < htmlString.length) {
    parts.push(htmlString.substring(currentIndex));
  }
  
  // If no HTML tags found, check for emails or return text as-is
  if (parts.length === 0) {
    const emailParts = linkEmailAddresses(htmlString);
    if (emailParts) {
      return emailParts.map((part, index) => {
        if (part.type === 'email') {
          return (
            <a
              key={`email-${index}`}
              href={`mailto:${part.email}`}
              className="chatbot-link"
            >
              {part.content}
            </a>
          );
        }
        return part.content;
      });
    }
    return [htmlString];
  }
  
  return parts;
};

const Chatbot = ({
  apiUrl = 'https://mykedragbot.vercel.app/api/chat',
  position = 'bottom-right',
  primaryColor = '#7c3aed'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const messagesEndRef = useRef(null);
  const conversationStartedRef = useRef(false);
  const conversationStartTimeRef = useRef(null);

  // Load question count from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mykeChat_questionCount');
    if (saved) {
      setQuestionCount(parseInt(saved, 10));
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Track conversation started when chatbot opens
  useEffect(() => {
    if (isOpen && !conversationStartedRef.current) {
      conversationStartedRef.current = true;
      conversationStartTimeRef.current = Date.now();
      const pageUrl = window.location.href;
      const pageHash = window.location.hash || 'none';
      const scrollY = window.scrollY;
      ReactGA.event({
        category: 'rag_chatbot',
        action: 'conversation_started',
        label: `trigger_user_initiated|page_url_${pageUrl.substring(0, 80)}|page_hash_${pageHash}|scroll_position_${scrollY}px`,
        value: 1
      });
    }
  }, [isOpen]);

  // Track conversation ended when chatbot closes (if conversation was active)
  useEffect(() => {
    if (!isOpen && conversationStartedRef.current && messages.length > 0) {
      const conversationDuration = conversationStartTimeRef.current 
        ? Date.now() - conversationStartTimeRef.current 
        : 0;
      const questionCount = messages.filter(m => m.role === 'user').length;
      const responseCount = messages.filter(m => m.role === 'assistant').length;
      const totalChars = messages.reduce((sum, m) => sum + m.content.length, 0);
      ReactGA.event({
        category: 'rag_chatbot',
        action: 'conversation_ended',
        label: `session_complete|total_messages_${messages.length}|questions_${questionCount}|responses_${responseCount}|total_chars_${totalChars}|duration_${Math.round(conversationDuration / 1000)}s`,
        value: messages.length
      });
      // Reset for next conversation
      conversationStartedRef.current = false;
      conversationStartTimeRef.current = null;
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input.trim();
    setInput('');
    setLoading(true);

    const newCount = questionCount + 1;
    setQuestionCount(newCount);
    localStorage.setItem('mykeChat_questionCount', newCount.toString());

      // Track message sent with detailed context
      const messageLength = currentInput.length;
      const wordCount = currentInput.trim().split(/\s+/).filter(word => word.length > 0).length;
      const questionMarkCount = (currentInput.match(/\?/g) || []).length;
      const isQuestion = questionMarkCount > 0 || currentInput.trim().toLowerCase().startsWith('what') || 
                         currentInput.trim().toLowerCase().startsWith('how') || 
                         currentInput.trim().toLowerCase().startsWith('why') ||
                         currentInput.trim().toLowerCase().startsWith('when') ||
                         currentInput.trim().toLowerCase().startsWith('where') ||
                         currentInput.trim().toLowerCase().startsWith('who');
      ReactGA.event({
        category: 'rag_chatbot',
        action: 'message_sent',
        label: `message_length_${messageLength}_chars|word_count_${wordCount}|question_marks_${questionMarkCount}|is_question_${isQuestion}|question_number_${newCount}|message_preview_${currentInput.substring(0, 80)}`,
        value: messageLength
      });

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: currentInput,
          conversationHistory: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          questionCount: newCount
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        links: data.links || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Track response received with detailed context
      const responseLength = data.response.length;
      const linkCount = data.links ? data.links.length : 0;
      const projectLinks = data.links ? data.links.filter(l => l.type === 'project').length : 0;
      const sectionLinks = data.links ? data.links.filter(l => l.type === 'section').length : 0;
      const externalLinks = data.links ? data.links.filter(l => l.type === 'external').length : 0;
      const wordCount = data.response.trim().split(/\s+/).filter(word => word.length > 0).length;
      ReactGA.event({
        category: 'rag_chatbot',
        action: 'response_received',
        label: `response_length_${responseLength}_chars|word_count_${wordCount}|total_links_${linkCount}|project_links_${projectLinks}|section_links_${sectionLinks}|external_links_${externalLinks}|conversation_length_${messages.length + 1}_messages`,
        value: responseLength
      });
    } catch (error) {
      console.error('Chat error:', error);
      
      // Track error with detailed context
      const errorMsg = error.message || 'unknown_error';
      const errorType = error.name || 'Error';
      const stackTrace = error.stack ? error.stack.substring(0, 100) : 'no_stack';
      ReactGA.event({
        category: 'rag_chatbot',
        action: 'error_occurred',
        label: `error_type_${errorType}|error_message_${errorMsg.substring(0, 80)}|stack_preview_${stackTrace}|conversation_length_${messages.length}_messages|question_count_${questionCount}`,
        value: 1
      });
      
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again later.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={`chatbot-widget chatbot-${position}`}>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          className="chatbot-toggle"
          onClick={() => setIsOpen(true)}
          style={{ backgroundColor: primaryColor }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>RAG Assistant</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window" style={{ borderColor: primaryColor }}>
          <div className="chatbot-header" style={{ backgroundColor: primaryColor }}>
            <div>
              <h3>RAG-Powered Assistant</h3>
              <p>I can help answer questions about Myke D's work, projects, and experience</p>
            </div>
            <button 
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="chatbot-welcome">
                <p>👋 Hi! I'm here to help answer any questions you have about Myke D's work, projects, and experience. What would you like to know?</p>
              </div>
            )}
            
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-message chatbot-message-${msg.role}`}>
                <div className="chatbot-message-content">
                  {renderMessage(msg.content, msg.links)}
                </div>
                <div className="chatbot-message-time">
                  {msg.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="chatbot-message chatbot-message-assistant">
                <div className="chatbot-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-container">
            <textarea
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about myke d."
              rows={1}
              disabled={loading}
            />
            <button
              className="chatbot-send"
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              style={{ backgroundColor: primaryColor }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
